import * as React from 'react';
import { type Control, type FieldPath, type FieldValues } from 'react-hook-form';
import { useSystems } from '@/api/queries/useSystems';
import { distinctValues, FormCreatableCombobox } from './FormCreatableCombobox';

export interface FormSystemCategorySelectProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> {
  /**
   * React Hook Form control object
   */
  control: Control<TFieldValues>;

  /**
   * Field name in the form
   */
  name: TName;

  /**
   * Field label
   */
  label?: string;

  /**
   * Placeholder text
   */
  placeholder?: string;
}

/**
 * React Hook Form adapter for the system category field.
 * Automatically fetches systems and suggests the distinct categories already
 * in use, while still allowing a brand-new value via "Create" — the same
 * search-or-create pattern as the external-contact type field.
 */
export function FormSystemCategorySelect<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(props: FormSystemCategorySelectProps<TFieldValues, TName>) {
  const { data: systems, isLoading } = useSystems();

  const categories = React.useMemo(
    () => distinctValues(systems?.map((system) => system.category)),
    [systems]
  );

  return <FormCreatableCombobox {...props} options={categories} isLoading={isLoading} />;
}

FormSystemCategorySelect.displayName = 'FormSystemCategorySelect';
